<template>
  <Content class="order-detail">
    <Card>
      <div slot="title">
        <Button type="text" icon="ios-arrow-back" @click="goBack">返回</Button>
        <span style="margin-left: 10px;">订单详情</span>
      </div>

      <Spin v-if="loading" fix></Spin>

      <div v-else-if="orderInfo">
        <!-- 订单基本信息 -->
        <Card title="订单信息" style="margin-bottom: 16px;">
          <Row :gutter="16">
            <Col span="12">
              <p><strong>订单ID：</strong>{{ orderInfo._id }}</p>
              <p><strong>订单号：</strong>{{ orderInfo.order_no || '-' }}</p>
              <p><strong>订单状态：</strong>
                <Tag :color="getStatusColor(orderInfo.command)">
                  {{ getStatusName(orderInfo.command) }}
                </Tag>
              </p>
              <p><strong>创建时间：</strong>{{ formatTime(orderInfo.create_time) }}</p>
            </Col>
            <Col span="12">
              <p><strong>订单金额：</strong><span style="color: #f56c6c; font-size: 18px;">¥{{ (orderInfo.total_price || 0).toFixed(2) }}</span></p>
              <p><strong>开始时间：</strong>{{ formatTime(orderInfo.start_time) }}</p>
              <p><strong>结束时间：</strong>{{ formatTime(orderInfo.return_time) }}</p>
              <p><strong>使用时长：</strong>{{ calculateDuration(orderInfo.start_time, orderInfo.return_time) }}</p>
            </Col>
          </Row>
        </Card>

        <!-- 状态流转图 -->
        <Card title="订单状态流转" style="margin-bottom: 16px;" v-if="orderInfo.statusFlow">
          <div class="status-flow">
            <div 
              v-for="(item, index) in orderInfo.statusFlow.flow" 
              :key="index"
              class="status-item"
              :class="{ 'completed': item.completed, 'current': item.current }"
            >
              <div class="status-dot" :style="{ backgroundColor: item.color }"></div>
              <div class="status-label">{{ item.statusName }}</div>
              <div v-if="index < orderInfo.statusFlow.flow.length - 1" class="status-line"></div>
            </div>
          </div>
        </Card>

        <!-- 状态历史记录 -->
        <Card title="状态历史记录" style="margin-bottom: 16px;" v-if="orderInfo.statusHistory && orderInfo.statusHistory.length > 0">
          <Timeline>
            <TimelineItem 
              v-for="(item, index) in orderInfo.statusHistory" 
              :key="index"
              :color="getStatusColor(item.status)"
            >
              <p><strong>{{ item.statusName }}</strong></p>
              <p>{{ formatTime(item.timestamp) }}</p>
              <p v-if="item.remark" style="color: #909399;">{{ item.remark }}</p>
            </TimelineItem>
          </Timeline>
        </Card>

        <!-- 用户信息 -->
        <Card title="用户信息" style="margin-bottom: 16px;" v-if="orderInfo.user_details">
          <Row :gutter="16">
            <Col span="12">
              <p><strong>用户昵称：</strong>{{ orderInfo.user_details.nickName || '-' }}</p>
              <p><strong>用户电话：</strong>{{ orderInfo.user_details.phone || '-' }}</p>
            </Col>
            <Col span="12">
              <p><strong>用户ID：</strong>{{ orderInfo.user_id }}</p>
              <Avatar 
                v-if="orderInfo.user_details.avatarUrl" 
                :src="orderInfo.user_details.avatarUrl" 
                size="large"
              />
            </Col>
          </Row>
        </Card>

        <!-- 车辆信息 -->
        <Card title="车辆信息" style="margin-bottom: 16px;" v-if="orderInfo.car_details">
          <Row :gutter="16">
            <Col span="12">
              <p><strong>车辆名称：</strong>{{ orderInfo.car_details.car_name || '-' }}</p>
              <p><strong>车辆编号：</strong>{{ orderInfo.car_ref || '-' }}</p>
              <p><strong>车辆状态：</strong>
                <Tag :color="orderInfo.car_details.online ? 'success' : 'default'">
                  {{ orderInfo.car_details.online ? '在线' : '离线' }}
                </Tag>
              </p>
            </Col>
            <Col span="12">
              <p><strong>电量：</strong>{{ orderInfo.car_details.battery || 0 }}%</p>
              <p><strong>区域：</strong>{{ orderInfo.area_details?.area_name || '-' }}</p>
              <p v-if="orderInfo.car_details?.location?.coordinates && orderInfo.car_details.location.coordinates.length >= 2">
                <strong>位置：</strong>
                {{ orderInfo.car_details.location.coordinates[1] }}, {{ orderInfo.car_details.location.coordinates[0] }}
              </p>
            </Col>
          </Row>
        </Card>

        <!-- 地址信息 -->
        <Card title="地址信息" style="margin-bottom: 16px;" v-if="orderInfo.address">
          <p><strong>标准地址：</strong>{{ orderInfo.address.standard_address || '-' }}</p>
          <p><strong>详细地址：</strong>{{ orderInfo.address.detail || '-' }}</p>
          <div v-if="orderInfo.address.location" style="margin-top: 16px;">
            <div id="mapContainer" style="width: 100%; height: 300px;"></div>
          </div>
        </Card>

        <!-- 支付信息 -->
        <Card title="支付信息" style="margin-bottom: 16px;" v-if="orderInfo.paymes">
          <Row :gutter="16">
            <Col span="12">
              <p><strong>支付金额：</strong>¥{{ formatPaymentAmount(orderInfo.paymes.amount) }}</p>
              <p><strong>支付方式：</strong>{{ orderInfo.paymes.paymentMethod || '微信支付' }}</p>
            </Col>
            <Col span="12">
              <p><strong>支付时间：</strong>{{ formatTime(orderInfo.paymes.paymentTime) }}</p>
              <p><strong>交易号：</strong>{{ orderInfo.paymes.transactionId || '-' }}</p>
            </Col>
          </Row>
        </Card>
      </div>

      <div v-else>
        <Empty description="订单不存在"></Empty>
      </div>
    </Card>
  </Content>
</template>

<script>
import { getOrderDetail } from '@/api/order'

export default {
  name: 'OrderDetail',
  data() {
    return {
      loading: true,
      orderInfo: null,
      map: null
    }
  },
  methods: {
    async loadOrderDetail() {
      this.loading = true
      try {
        const orderId = this.$route.params.id
        if (!orderId) {
          this.$Message.error('订单ID不能为空')
          this.loading = false
          return
        }
        
        const res = await getOrderDetail(orderId)
        // 检查响应格式：后端返回 { success: true, code: 200, message: '...', data: {...} }
        if (res.data && (res.data.success === true || res.data.code === 200) && res.data.data) {
          this.orderInfo = res.data.data
          // 延迟加载地图，确保DOM已渲染
          this.$nextTick(() => {
            this.initMap()
          })
        } else {
          const errorMsg = res.data?.message || res.data?.msg || '获取订单详情失败'
          this.$Message.error(errorMsg)
          console.error('获取订单详情失败:', res.data)
        }
      } catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || '获取订单详情失败，请稍后重试'
        this.$Message.error(errorMsg)
        console.error('获取订单详情异常:', error)
      } finally {
        this.loading = false
      }
    },
    initMap() {
      // 如果有地址信息，初始化地图
      if (this.orderInfo?.address?.location?.coordinates && !this.map) {
        const location = this.orderInfo.address.location.coordinates
        // 检查坐标是否有效
        if (!Array.isArray(location) || location.length < 2) {
          console.warn('地址坐标格式无效:', location)
          return
        }
        // 这里需要根据你使用的地图服务（高德、百度、腾讯等）初始化地图
        // 示例使用高德地图
        if (window.AMap) {
          try {
            this.map = new window.AMap.Map('mapContainer', {
              zoom: 15,
              center: [location[0], location[1]]
            })
            new window.AMap.Marker({
              position: [location[0], location[1]],
              map: this.map
            })
          } catch (error) {
            console.error('地图初始化失败:', error)
          }
        } else {
          console.warn('地图SDK未加载，请检查地图服务配置')
        }
      }
    },
    getStatusName(status) {
      const statusMap = {
        0: '待分配',
        1: '已分配',
        2: '前往中',
        3: '已到达',
        4: '使用中',
        5: '归还中',
        6: '已完成',
        7: '已取消',
        8: '故障'
      }
      return statusMap[status] || '未知'
    },
    getStatusColor(status) {
      const colorMap = {
        0: 'default',
        1: 'primary',
        2: 'success',
        3: 'warning',
        4: 'error',
        5: 'default',
        6: 'success',
        7: 'error',
        8: 'error'
      }
      return colorMap[status] || 'default'
    },
    formatTime(time) {
      if (!time) return '-'
      try {
        return new Date(time).toLocaleString('zh-CN')
      } catch (error) {
        console.error('时间格式化失败:', error)
        return '-'
      }
    },
    formatPaymentAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      const numAmount = typeof amount === 'number' ? amount : parseFloat(amount)
      if (isNaN(numAmount)) return '0.00'
      // 如果金额已经是元为单位，直接格式化；如果是分为单位，需要除以100
      // 根据业务逻辑判断：如果金额大于10000，可能是分；否则可能是元
      const finalAmount = numAmount > 10000 ? numAmount / 100 : numAmount
      return finalAmount.toFixed(2)
    },
    calculateDuration(startTime, endTime) {
      if (!startTime || !endTime) return '-'
      try {
        const duration = new Date(endTime) - new Date(startTime)
        if (duration < 0) return '-'
        const minutes = Math.floor(duration / 1000 / 60)
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        if (hours > 0) {
          return `${hours}小时${mins}分钟`
        }
        return `${mins}分钟`
      } catch (error) {
        console.error('时长计算失败:', error)
        return '-'
      }
    },
    goBack() {
      this.$router.go(-1)
    }
  },
  mounted() {
    this.loadOrderDetail()
  }
}
</script>

<style lang="less" scoped>
.order-detail {
  .status-flow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0;
    position: relative;

    .status-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;

      .status-dot {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid #fff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        z-index: 2;
      }

      .status-label {
        margin-top: 8px;
        font-size: 12px;
        color: #909399;
      }

      .status-line {
        position: absolute;
        top: 12px;
        left: 50%;
        width: 100%;
        height: 2px;
        background: #e4e7ed;
        z-index: 1;
      }

      &.completed {
        .status-dot {
          border-color: #67c23a;
        }
        .status-label {
          color: #67c23a;
          font-weight: bold;
        }
        .status-line {
          background: #67c23a;
        }
      }

      &.current {
        .status-dot {
          transform: scale(1.2);
          box-shadow: 0 0 8px rgba(64, 158, 255, 0.5);
        }
        .status-label {
          color: #409eff;
          font-weight: bold;
        }
      }

      &:last-child .status-line {
        display: none;
      }
    }
  }
}
</style>

